import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { Avatar, AvatarGroup, Box, Container, Divider, Grid2 as Grid, useMediaQuery, useTheme } from "@mui/material";
import {
    Done as DoneIcon,
    Report as ReportIcon,
    ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { grey, pink, orange } from "@mui/material/colors";

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    BasicLoader, StatusChip, Typography,
    WorkAndSafetyIcon, LabelledProfileImage,
    FilledButton,
    FilePreview,
    BottomSearchAppBar,
    TextButton
} from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, formatDisplayString } from "@initia-digital/ts-utils";
import { compareHazardsByRiskLevel } from "@initia-digital/data-service-mapping";

import { BasicAppBarPage } from "../../../../../../layouts/BasicAppBarPage";
import {
    useFullTeamList,
    useHazardClassList,
    useIsAdmin, useJobSearch,
    usePlanDetail,
    usePrestartDetail
} from "../../../../../../hooks";
import { getS3Object } from "../../../../../../api/objectStorage";
import { EmergencyDirectiveBanner } from "../../../../../../legacy/EmergencyDirectiveBanner";
import { fetchJobDetail, fetchPrestartPdf } from "../../../../../../api/query";
import { sortHazardClassesByPresentationOrder, sortPlanRegisterHazardClassPresentationOrder } from "../../../../../../utils";
import { muiGreen } from "@initia-digital/colors";
import { useWorkAndSafetyStore } from "../../../../../../store";

// Create a custom error class for prestart loading errors
class PrestartLoadingError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'PrestartLoadingError';
    }
}

const ViewPrestartDetail = () => {
    // ---------------------
    const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
    const [isPdfLoading, setIsPdfLoading] = useState(false);

    const {
        updateEditPrestartState
    } = useWorkAndSafetyStore();

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));

    const { jobId, planId, prestartId } = useParams();
    const navigate = useNavigate();

    const authDetail = useMSALPayload();
    const isAdmin = useIsAdmin(authDetail, true);

    const searchJob = useJobSearch(authDetail);

    // DATA INTEGRATION
    const { fullTeamListData } = useFullTeamList(authDetail, true);

    // JOB DETAIL
    const { data: jobDetail } = useQuery({
        queryKey: ['job', jobId],
        queryFn: () => fetchJobDetail(authDetail, jobId!),
        enabled: baseDefinedAndNotNullNarrowing(jobId),
        refetchOnWindowFocus: true,
        retry: 2
    });
    const { jobName } = jobDetail ?? { jobName: undefined };


    // PLAN DETAIL
    const { planDetailData } = usePlanDetail(
        authDetail, planId!,
        baseDefinedAndNotNullNarrowing(planId),
        0
    );
    const {
        teamMembers,
        criticalHazards,
        hazardsFromRegister
    } = planDetailData ?? {};

    const { hazardClassListData } = useHazardClassList(authDetail, true, { status: 'ACTIVE' });
    const hazardClassListAreDefined = baseDefinedAndNotNullNarrowing(hazardClassListData);

    const referenceHazardClassPresentationOrder = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return [...hazardClassListData].sort(sortHazardClassesByPresentationOrder).map(hazardClass => hazardClass.label);
    }, [hazardClassListAreDefined, hazardClassListData]);

    const registerHazardsForPresentation = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return [...(hazardsFromRegister ?? [])]
            .sort((a, b) => compareHazardsByRiskLevel(a, b))
            .sort((a, b) => sortPlanRegisterHazardClassPresentationOrder(a, b, referenceHazardClassPresentationOrder));
    }, [hazardClassListAreDefined, hazardsFromRegister, referenceHazardClassPresentationOrder]);

    const hazardClassGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return {};
        return Object.groupBy(registerHazardsForPresentation, (hazard) => hazard.hazardClass.label);
    }, [registerHazardsForPresentation, hazardClassListAreDefined]);

    const registerHazardGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return Object.keys(hazardClassGroups);
    }, [hazardClassGroups, hazardClassListAreDefined]);

    // PRESTART DETAIL
    const { prestartDetailData, prestartDetailLoading } = usePrestartDetail(
        authDetail, prestartId!,
        baseDefinedAndNotNullNarrowing(prestartId),
        0
    );
    const {
        prestartTimestamp, leadingTeam,
        status,
        createdBy: prestartCreatedBy,
        teamsHighlightedHazards,
        newHazardsAndControls,
        teamWorkDetail
    } = prestartDetailData ?? {};

    let prestartLabel = 'Take-5';
    if (leadingTeam) {
        prestartLabel = 'prestart meeting';
    }

    const prestartTitle = baseDefinedAndNotNullNarrowing(jobName) ? `${formatDisplayString(prestartLabel, { capitalizeFirst: true })} for ${jobName}` : prestartLabel;

    const fullTeamListAreDefined = baseDefinedAndNotNullNarrowing(fullTeamListData);

    const createdByIdentity = useMemo(() => {
        // ---------------------
        if (!fullTeamListAreDefined) return null;
        return fullTeamListData.find(item => item.userPrincipalName.toLowerCase() === prestartCreatedBy?.toLowerCase());
    }, [fullTeamListAreDefined, fullTeamListData, prestartCreatedBy]);

    const teamMembersForDisplay = useMemo(() => {
        // ---------------------
        if (!fullTeamListAreDefined) return [];
        return fullTeamListData.filter(item => (teamMembers ?? []).includes(item.displayName));
    }, [fullTeamListAreDefined, fullTeamListData, teamMembers]);

    // EFFECTS
    // Initialize edit prestart state from prestart detail
    // ---------------------
    useEffect(() => {
        // When you land on the edit-prestart page, you want to load the prestart detail fresh in all instances
        // 1. The jobId or planId has changed (i.e. this is a different plan to what is already held in state)
        if (
            baseDefinedAndNotNullNarrowing(jobId) && baseDefinedAndNotNullNarrowing(planId)
            && prestartDetailData && !prestartDetailLoading
        ) {
            // ---------------------
            updateEditPrestartState({
                ...prestartDetailData
            });
        }
    }, [prestartDetailData, jobId, planId, prestartId, prestartDetailLoading, updateEditPrestartState]);


    useEffect(() => {
        const loadFileUrl = async () => {
            if (!pdfUrl && !isPdfLoading) {
                const loadPdfUrl = async () => {
                    setIsPdfLoading(true);
                    try {
                        const pdf = await fetchPrestartPdf(authDetail, jobId!, planId!, prestartId!);
                        const { path } = pdf;

                        if (!baseDefinedAndNotNullNarrowing(path)) {
                            setPdfUrl(undefined);
                            navigate(`/job/${jobId}`, {
                                state: {
                                    error: new PrestartLoadingError('Prestart not found'),
                                    from: 'prestart'
                                }
                            });
                            return;
                        }

                        const response = await getS3Object(authDetail, { path });

                        if (response?.buffer) {
                            try {
                                const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
                                const url = URL.createObjectURL(blob);
                                return url;
                            } catch (error) {
                                console.error('Error creating object URL:', error);
                                navigate(`/job/${jobId}`, {
                                    state: {
                                        error: new PrestartLoadingError(`Error processing prestart: ${prestartDetailData?.id ?? 'unknown id'}`),
                                        from: 'prestart'
                                    }
                                });
                                return;
                            } finally {
                                setIsPdfLoading(false);
                            }
                        } else {
                            setIsPdfLoading(false);
                            setPdfUrl(undefined);
                            navigate(`/job/${jobId}`, {
                                state: {
                                    error: new PrestartLoadingError('File upload is missing buffer'),
                                    from: 'prestart'
                                }
                            });
                            return;
                        }
                    } catch (error) {
                        setIsPdfLoading(false);
                        if (error instanceof PrestartLoadingError) {
                            navigate(`/job/${jobId}`, {
                                state: {
                                    error,
                                    from: 'prestart'
                                }
                            });
                            return;
                        }
                        console.error('Failed to load file URL:', error);
                        navigate(`/job/${jobId}`, {
                            state: {
                                error: new PrestartLoadingError('Failed to load prestart PDF'),
                                from: 'prestart'
                            }
                        });
                        return;
                    }
                };

                try {
                    const loadedUrl = await loadPdfUrl();
                    if (loadedUrl) {
                        setPdfUrl(loadedUrl);
                    }
                } catch (error) {
                    navigate(`/job/${jobId}`, {
                        state: {
                            error,
                            from: 'prestart'
                        }
                    });
                } finally {
                    setIsPdfLoading(false);
                }
            }
        };

        loadFileUrl();
    }, [authDetail, pdfUrl, jobId, planId, prestartId, prestartDetailData?.id, navigate, isPdfLoading]);


    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="xl" sx={{ px: !isSmallerThanMd ? "25px" : "10px" }}>
                <EmergencyDirectiveBanner />
                <Grid
                    container
                    columnSpacing={1.5} rowSpacing={2} alignItems="stretch"
                    mb="32px"
                >
                    <Grid
                        size={{ xs: 12, md: 6, lg: 5 }}
                        height="auto"
                        border="1px solid lightgrey" borderRadius="5px"
                    >
                        {
                            prestartDetailLoading && (
                                <BasicLoader
                                    size={50}
                                />
                            )
                        }
                        {
                            !prestartDetailLoading &&
                            baseDefinedAndNotNullNarrowing(prestartDetailData) && (
                                <Box
                                    sx={{
                                        p: '8px 16px',
                                        paddingBottom: '4px',
                                    }}
                                >
                                    <Box
                                        pt="15px" pb="10px" px="10px"
                                        flex={1} display="flex" flexDirection="column"
                                    >
                                        <Box display="flex" mb="10px">
                                            <TextButton
                                                color={theme.palette.primary.contrastText}
                                                hoverBackgroundColor={theme.palette.primary.main}
                                                onClick={() => {
                                                    navigate(`/job/${jobId}`)
                                                }}
                                                startIcon={<ArrowBackIcon />}
                                            >
                                                {`Back to ${jobId}`}
                                            </TextButton>
                                        </Box>
                                        <Box
                                            flex={1} display="flex" flexDirection="row" alignItems="center"
                                        >
                                            <Box
                                                alignSelf="stretch"
                                                display="flex" flexDirection="column" alignItems="center"
                                            >
                                                <WorkAndSafetyIcon
                                                    size={isSmallerThanSm ? 80 : 80}
                                                    type="prestart_document"
                                                />
                                            </Box>
                                            <Box
                                                flex={1}
                                                display='flex' flexDirection="column"
                                                px="15px"
                                            >
                                                <Box>
                                                    <Typography
                                                        fontSize="0.8rem"
                                                        fontWeight={700}
                                                    >
                                                        {prestartTitle}
                                                    </Typography>
                                                </Box>
                                                {
                                                    baseDefinedAndNotNullNarrowing(prestartTimestamp) && (
                                                        <Box sx={{ lineHeight: 0 }}>
                                                            <Typography
                                                                fontSize={"0.6rem"}
                                                                color={theme.palette.primary.contrastText}
                                                                fontWeight={300}
                                                            >
                                                                {`${DateTime.fromISO(prestartTimestamp).toFormat('dd/MM/yyyy')}`}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                }
                                                {
                                                    baseDefinedAndNotNullNarrowing(status) && (
                                                        <Box
                                                            py="2px"
                                                            display="flex" flexDirection="row" alignItems="center"
                                                        >
                                                            <StatusChip
                                                                type="prestart"
                                                                id={baseDefinedAndNotNullNarrowing(prestartId) ? prestartId : ''}
                                                                status={status}
                                                                isAdmin={isAdmin}
                                                            />
                                                        </Box>
                                                    )
                                                }
                                            </Box>
                                        </Box>
                                        <Box
                                            mt="5px"
                                            py="5px"
                                            px="10px"
                                            bgcolor={grey[200]}
                                        >
                                            <Box
                                                display="flex" flexDirection="row" alignItems="center"
                                                py="2px" gap="2px"
                                            >
                                                <FilledButton
                                                    size="vsmall"
                                                    backgroundColor={theme.palette.secondary.main}
                                                    color={theme.palette.secondary.contrastText}
                                                    onClick={() => navigate(`/job/${jobId}/plan/${planId}/edit-prestart/${prestartId}`)}
                                                >
                                                    {`EDIT`}
                                                </FilledButton>
                                                <FilledButton
                                                    size="vsmall"
                                                    backgroundColor={theme.palette.secondary.main}
                                                    color={theme.palette.secondary.contrastText}
                                                    onClick={() => navigate(`/job/${jobId}/view-plan/${planId}`)}
                                                >
                                                    {`VIEW PLAN`}
                                                </FilledButton>
                                            </Box>
                                        </Box>
                                        <Box
                                            mb="15px"
                                        >
                                            <Divider />
                                        </Box>
                                        <Box
                                            display="flex" flexDirection="row" alignItems="flex-start"
                                            px="10px"
                                        >
                                            {
                                                baseDefinedAndNotNullNarrowing(createdByIdentity) && (
                                                    <Box
                                                        display="flex" flexDirection="row" alignItems="center" gap={1}
                                                    >
                                                        <LabelledProfileImage
                                                            label={'Created by'}
                                                            avatarVariant='circular'
                                                            avatarSize={40}
                                                            src={createdByIdentity.profileImage ?? undefined}
                                                            fontSize="0.5rem"
                                                            color={theme.palette.primary.contrastText}
                                                            labelPosition='top'
                                                            labelMargin={0}
                                                        />
                                                    </Box>
                                                )
                                            }
                                            {
                                                baseDefinedAndNotNullNarrowing(teamMembersForDisplay) && teamMembersForDisplay.length > 0 && (
                                                    <Box
                                                        px="20px"
                                                        lineHeight={0}
                                                    >
                                                        <Box>
                                                            <Typography
                                                                fontSize={"0.5rem"}
                                                                fontWeight={300}
                                                                color={theme.palette.primary.contrastText}
                                                            >
                                                                {`Plan team members`}
                                                            </Typography>
                                                        </Box>
                                                        <AvatarGroup
                                                            max={5}
                                                            total={teamMembersForDisplay.length}
                                                            slotProps={{
                                                                surplus: {
                                                                    sx: {
                                                                        width: '38px',
                                                                        height: '38px'
                                                                    }
                                                                }
                                                            }}
                                                            renderSurplus={(surplus) => {
                                                                return (
                                                                    <Avatar
                                                                        sx={{
                                                                            width: '38px', height: '38px',
                                                                            fontSize: '0.9rem'
                                                                        }}
                                                                    >
                                                                        {`+${surplus}`}
                                                                    </Avatar>
                                                                )
                                                            }}
                                                        >
                                                            {
                                                                teamMembersForDisplay.map((member, idx) => (
                                                                    <Avatar
                                                                        key={`team-member-${idx}`}
                                                                        sx={{
                                                                            width: '38px',
                                                                            height: '38px'
                                                                        }}
                                                                        src={member.profileImage ?? undefined}
                                                                        alt={member.displayName}
                                                                    />
                                                                ))
                                                            }
                                                        </AvatarGroup>
                                                    </Box>
                                                )
                                            }
                                        </Box>
                                        <Box px="10px" mt="10px">
                                            <Typography
                                                fontSize={"0.75rem"}
                                                fontWeight={700}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                {`${formatDisplayString(prestartLabel, { capitalizeFirst: true })}`}
                                            </Typography>
                                        </Box>
                                        <Box px="15px">
                                            {
                                                [...(teamsHighlightedHazards ?? []), ...(newHazardsAndControls ?? [])].length === 0 && (
                                                    <Box
                                                        display="flex" flexDirection="row" alignItems="center"
                                                        gap={1}
                                                    >
                                                        <DoneIcon sx={{ color: muiGreen['500'] }} />
                                                        <Typography
                                                            fontSize={"0.75rem"} color={muiGreen['500']}
                                                            fontWeight={500}
                                                        >
                                                            {`No new or changed hazards were recorded during this ${prestartLabel}`}
                                                        </Typography>
                                                    </Box>
                                                )
                                            }
                                            {
                                                [...(teamsHighlightedHazards ?? []), ...(newHazardsAndControls ?? [])].length > 0
                                                && (
                                                    <Box
                                                        display="flex" flexDirection="column"
                                                        px="4px"
                                                        sx={{
                                                            containerType: 'inline-size',
                                                            containerName: `critical-hazard-plan-summary`,
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex" flexDirection="row" gap={1} alignItems="center"
                                                            pb="5px"
                                                        >
                                                            <Typography
                                                                color={theme.palette.secondary.main}
                                                                fontSize="0.7rem"
                                                                fontWeight={500}
                                                            >
                                                                {`New or changed hazards (${[...(teamsHighlightedHazards ?? []), ...(newHazardsAndControls ?? [])].length})`}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )
                                            }
                                        </Box>
                                        <Box px="19px" lineHeight="0">
                                            <Typography
                                                fontSize={"0.75rem"}
                                                fontWeight={500}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                Overlapping duties
                                            </Typography>
                                        </Box>
                                        <Box px="19px" lineHeight="0">
                                            <Typography
                                                fontSize={"0.6rem"}
                                                color={grey[600]}
                                                fontWeight={300}
                                                align="justify"
                                            >
                                                {!baseDefinedAndNotNullNarrowing(teamWorkDetail) || teamWorkDetail === '' ? 'No overlapping duties or team-work detail recorded' : teamWorkDetail}
                                            </Typography>
                                        </Box>
                                        <Box px="10px">
                                            <Typography
                                                fontSize={"0.75rem"}
                                                fontWeight={700}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                Plan
                                            </Typography>
                                        </Box>
                                        <Box px="15px" lineHeight="0">
                                            {
                                                (criticalHazards ?? []).length === 0 && (
                                                    <Box
                                                        display="flex" flexDirection="row" alignItems="center"
                                                        my="5px" gap={1}
                                                    >
                                                        <ReportIcon sx={{ color: orange['500'] }} />
                                                        <Typography
                                                            fontSize={"0.75rem"} color={orange['500']}
                                                        >
                                                            No highlighted or site specific hazards recorded
                                                        </Typography>
                                                    </Box>
                                                )
                                            }
                                            {
                                                (criticalHazards ?? []).length > 0
                                                && (
                                                    <Box
                                                        display="flex" flexDirection="column"
                                                        px="4px"
                                                        sx={{
                                                            containerType: 'inline-size',
                                                            containerName: `critical-hazard-plan-summary`,
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex" flexDirection="row" gap={1} alignItems="center"
                                                            pb="5px"
                                                        >
                                                            <Typography
                                                                color={theme.palette.secondary.main}
                                                                fontSize="0.7rem"
                                                                fontWeight={500}
                                                            >
                                                                {`Highlighted or site specific hazards (${(criticalHazards ?? []).length})`}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )
                                            }
                                            {
                                                (hazardsFromRegister ?? []).length === 0 && (
                                                    <Box
                                                        display="flex" flexDirection="row" alignItems="center" gap={1}
                                                    >
                                                        <ReportIcon sx={{ color: pink['500'] }} />
                                                        <Typography
                                                            fontSize={"0.75rem"} color={pink['500']}
                                                        >
                                                            No register hazards recorded
                                                        </Typography>
                                                    </Box>
                                                )
                                            }
                                            {
                                                (hazardsFromRegister ?? []).length > 0
                                                && (
                                                    <Box
                                                        display="flex" flexDirection="column"
                                                        px="4px"
                                                        sx={{
                                                            containerType: 'inline-size',
                                                            containerName: `register-hazard-plan-summary`,
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex" flexDirection="row" gap={1} alignItems="center"
                                                        >
                                                            <Typography
                                                                color={theme.palette.secondary.main}
                                                                fontSize="0.7rem"
                                                                fontWeight={500}
                                                            >
                                                                {`Register hazard groups (${registerHazardGroups.length} group${registerHazardGroups.length === 1 ? '' : 's'}, ${(hazardsFromRegister ?? []).length} hazard${(hazardsFromRegister ?? []).length === 1 ? '' : 's'})`}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )
                                            }
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        }
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 7 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box
                            display="flex" flexDirection="column" width="100%"
                            px="10px"
                        >
                            <Typography
                                variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                color={theme.palette.secondary.main} fontWeight={700}
                            >
                                Preview
                            </Typography>
                        </Box>
                        <Box
                            px="10px"
                        >
                            <Typography
                                fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                You can download a PDF copy by clicking 'download' below
                            </Typography>
                        </Box>
                        <Box
                            px="10px"
                        >
                            {
                                isPdfLoading ? (
                                    <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                                        <Typography fontSize="0.8rem">Building a PDF for the prestart</Typography>
                                        <BasicLoader size={50} />
                                    </Box>
                                ) : (
                                    <FilePreview url={pdfUrl} filename={`${DateTime.local().toFormat('yyyyMMdd_HHmmss')}-${jobId}-${prestartLabel.toLowerCase()}.pdf`} />
                                )
                            }
                        </Box>
                    </Grid>
                </Grid>
                <BottomSearchAppBar
                    placeholder='Search for a job'
                    label='Search for a Job'
                    labelKey='jobId'
                    secondaryKeysForDisplay={['jobId']}
                    suggestionKeysForOptionDisplay={['jobId', 'jobName']}
                    suggestionKeysToSearchOption={['jobId', 'jobName']}
                    uniqueOptionKeyProps={['jobId']}
                    handleChange={(value) => { navigate(`/job/${value.jobId}`) }}
                    searchSuggestion={searchJob}
                />
            </Container>
        </BasicAppBarPage>
    )
}

export default ViewPrestartDetail;